import { useCallback, useMemo, useRef, useState } from 'react'

/**
 * `base` widened by `keys`, or `null` where every one of them is already open.
 *
 * The `null` answer is what leaves the state alone when a widening has nothing
 * to do, so a default that grows as branches come and go survives every call
 * until something is genuinely opened.
 */
export function widenBranches(
  base: ReadonlySet<string>,
  keys: readonly string[],
): Set<string> | null {
  if (keys.every(key => base.has(key))) return null
  return new Set([...base, ...keys])
}

/**
 * Which branches of a tree list are open, with everything open until someone
 * shuts one.
 *
 * A tree of nothing but headings answers no question the page is opened with,
 * so the default is not stored: `null` means nobody has chosen yet, and the
 * fallback widens as branches come and go. The first toggle replaces it with a
 * set the caller owns from then on.
 *
 * Reaching the branches that hold the selected row is `DataTableProps.ancestorsOf`
 * and belongs to the table; `openBranches` is for the rest — a row that has to
 * be reachable for a reason the selection does not name.
 */
export function useTreeExpansion(defaultOpenKeys: string[]) {
  const [openKeys, setOpenKeys] = useState<Set<string> | null>(null)
  // Keyed on the joined names, so a caller hands in a plain `map` without
  // having to memoise it.
  const fingerprint = defaultOpenKeys.join('|')

  const expandedKeys = useMemo(
    () => openKeys ?? new Set(fingerprint ? fingerprint.split('|') : []),
    [openKeys, fingerprint],
  )

  // Read at call time, so opening a branch is a stable callback: it is a
  // dependency of `rowActions`, and a churning one defeats the row memo.
  const current = useRef(expandedKeys)
  current.current = expandedKeys

  /**
   * Opens branches without touching the rest — for a row that has to be
   * reachable whether or not the reader put it there, such as the folder a
   * row action just created something in.
   */
  const openBranches = useCallback((keys: readonly string[]) => {
    const widened = widenBranches(current.current, keys)
    if (widened) setOpenKeys(widened)
  }, [])

  return { expandedKeys, setOpenKeys, openBranches }
}
